import 'normalize.css'
import 'flexboxgrid/src/css/flexboxgrid.css'
import 'styles/base.css'
import './index.css'

import cn from 'clsx'
import Storage from 'disco-storage'
import isEqual from 'lodash/isEqual'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import type { Location } from 'react-router-dom'

import { PHONE, TABLET } from '@disco/constants/DeviceTypes'
import {
  toggleShortlists,
  toggleSidebar,
} from '@disco/state-management/actions/AppActions'
import { getIsOnboarding } from '@disco/state-management/selectors/app'
import {
  getIsTrial,
  isFeatureEnabled,
} from '@disco/state-management/selectors/business'
import { getTrackPlaying } from '@disco/state-management/selectors/media'
import { getUser } from '@disco/state-management/selectors/user'
import NotificationManager from '@disco/universal-components/NotificationManager'

import ErrorBoundary from 'components/common/error-boundary'
import Player from 'components/common/player'
import SessionWatcher from 'components/common/service/session-watcher'
import TrialWatcher from 'components/common/trial-watcher'
import UINotificationSystem from 'components/common/ui-notification-system'
import CustomCss from 'components/custom-css'
import Header from 'components/header'
import HelpMenu from 'components/help-menu'
import CookieBanner from 'components/modals/cookie-banner'
import Offline from 'components/offline'
import ShortlistStack from 'components/shortlist-stack'
import Sidebar from 'components/sidebar'
import TrackActions from 'components/track-actions'
import UpdateInformer from 'components/update-informer'

import CustomDragLayer from 'containers/CustomDragLayer'
import { PreloadManager } from 'containers/PreloadManager'

import Home from 'pages/Home'

import { useDispatch, useSelector } from 'hooks/use-redux'

import type { RootState } from 'store/configureStore'

import MobileBreakpoints from './MobileBreakpoints'
import { useBanners } from './use-banners'

const enabledLocations = [
  '^/shortlists/.*',
  '^/selection/.*',
  '^/explorer/.*',
  '^/inboxes/.*',
  '^/referral-invite/.*',
  '^/catalogs/.*',
  '^/channels/.*',
  '^/pricing',
  '^/demo',
  '^/webinar',
]

type PlaylistCreatorLocation = 'Explorer' | 'Search' | 'Discover' | undefined
export const PLAYLIST_LOCATIONS: Record<
  PlaylistCreatorLocation,
  PlaylistCreatorLocation
> = {
  Explorer: 'Explorer',
  Search: 'Search',
  Discover: 'Discover',
}
const appRedirect = () => {
  if (typeof __DEBUG__ !== 'undefined' && __DEBUG__) return
  window.onbeforeunload = null
  const { protocol, host } = window.location
  setTimeout(() => window.location.assign(`${protocol}//${host}`), 3000)
}

type Props = {
  children: React.ReactNode
  location: Location
}

export default function AppWrapper(props: Props) {
  const app = useSelector((state: RootState) => state.app)
  const {
    isSearchPageShortlistOpen,
    shortlistUserSettings,
    shortlistsOpen,
    playlistCreatorWidth,
  } = app

  const [updatedPlaylistLocation, setUpdatedPlaylistLocation] =
    useState<PlaylistCreatorLocation>(undefined)
  const user = useSelector(getUser)
  const playerVisible = useSelector(getTrackPlaying)
  const isSubscriber = !useSelector(getIsTrial)
  const isOnboarding = useSelector(getIsOnboarding)
  const isSupportMenuFeatureEnabled = useSelector((state: RootState) =>
    isFeatureEnabled(state, {
      featureName: 'SUPPORT_MENU',
    }),
  )
  const isSidebarOpen = app.sidebarOpen && !isOnboarding
  const handler = props.children
  let header = null
  let notificationComponents = null
  const { pathname } = props.location
  let isPublicPage =
    pathname.indexOf('/upload') === 0 ||
    pathname.indexOf('/download') === 0 ||
    pathname.indexOf('/brief/') === 0
  let isLoginPage = isPathnameLoginRoute(pathname)
  let isSettingsPage = pathname.indexOf('/settings') === 0

  const isDiscoverPage = pathname.startsWith('/discover')
  const isHeaderVisible = !isPathnameWithHiddenHeader(pathname)
  const isSearchPage = pathname.indexOf('/search') === 0
  const isPreviewPage =
    pathname.startsWith('/brief-editor') && pathname.endsWith('/preview')

  const enableHeaderBlocks =
    pathname === '/' || enabledLocations.some(l => pathname.match(l))

  const banners = useBanners()
  const bannersVisible = banners.length
  const sidebarCanSlide = !app.launchedOnMobileDevice // && hasSidebar
  const isExplorerLayoutPage =
    pathname.indexOf('/explorer') === 0 ||
    pathname.indexOf('/brief') === 0 ||
    pathname.indexOf('/selection') === 0 ||
    pathname === '/' ||
    pathname.indexOf('/shortlists') === 0 ||
    pathname.indexOf('/inboxes') === 0 ||
    pathname.indexOf('/discover') === 0

  // Show Home component (Explorer) only on pages that actually need it
  const showHomeComponent = isExplorerLayoutPage

  // hide playlist creator for selected brief preview, edit and share pages
  const isBriefPage =
    pathname.indexOf('/brief/') === 0 || pathname.indexOf('/brief-editor') === 0
  const showPlaylistCreator =
    !isOnboarding && !isBriefPage && (isSearchPage || isExplorerLayoutPage)
  const dispatch = useDispatch()

  const getPlaylistCreatorLocation = (): PlaylistCreatorLocation => {
    if (isExplorerLayoutPage) return 'Explorer'
    if (isDiscoverPage) return 'Discover'
    if (isSearchPage) return 'Search'
    return undefined
  }

  const playlistCreatorSettings = useMemo(() => {
    const location = getPlaylistCreatorLocation()

    if (!location) return null
    const locationKey = location.toLowerCase()
    const isOpen = isSearchPage ? isSearchPageShortlistOpen : shortlistsOpen
    return {
      location: PLAYLIST_LOCATIONS[location],
      isOpen,
      settings: shortlistUserSettings.settings[locationKey],
    }
  }, [pathname])

  const handleStoredShortlistsOpen = useCallback(async () => {
    if (!playlistCreatorSettings) return
    const { location, isOpen, settings } = playlistCreatorSettings || {}
    const previousPageSettings =
      shortlistUserSettings?.settings?.[
        updatedPlaylistLocation?.toLowerCase() || ''
      ]
    const [storedShortlistsOpen, storedSearchPageShortlistOpen] =
      await Promise.all([
        Storage.get('playlist_creator_open'),
        Storage.get('search_page_playlist_creator_open'),
      ])

    let storedShortlistSettings = null
    if (location === 'Explorer') storedShortlistSettings = storedShortlistsOpen
    if (location === 'Search')
      storedShortlistSettings = storedSearchPageShortlistOpen

    const isShortlistSettingsStoredShortlistSettingsUpdated =
      storedShortlistSettings !== null &&
      !isEqual(storedShortlistSettings, isOpen)

    const isLocationChanged = updatedPlaylistLocation
      ? !isEqual(location, updatedPlaylistLocation)
      : false

    const isVisibilityChanged = !isEqual(
      settings?.visible,
      previousPageSettings?.visible,
    )

    const forcedState =
      storedShortlistSettings !== null ? storedShortlistSettings : isOpen

    const isToggleOnPageLoadEnabled = Boolean(
      isLocationChanged && isVisibilityChanged,
    )
    setUpdatedPlaylistLocation(location)

    if (
      !isToggleOnPageLoadEnabled &&
      !isShortlistSettingsStoredShortlistSettingsUpdated
    )
      return
    dispatch(toggleShortlists(forcedState, location, 'Load Page'))
  }, [dispatch, playlistCreatorSettings])

  useEffect(() => {
    handleStoredShortlistsOpen()
  }, [handleStoredShortlistsOpen, pathname])

  useEffect(() => {
    const bodyClassName = cn(app.os, {
      'is-mobile': app.launchedOnMobileDevice,
      'is-tablet': app.mobileDeviceType === TABLET,
      'is-phone': app.mobileDeviceType === PHONE,
    })
    // Checking if document.body exists before assigning className to it
    if (document?.body) document.body.className = bodyClassName
  }, [])
  const bannersHeightPx = banners
    .map(banner => banner.heightPx)
    .reduce((prevHeight, currentHeight) => prevHeight + currentHeight, 0)

  if (user.loggedIn && !isPublicPage && !isLoginPage) {
    header = !isOnboarding && (
      <Header
        fullWidthHeader={!sidebarCanSlide}
        isSubscriber={isSubscriber}
        bannersHeightPx={bannersHeightPx}
        sidebarOpen={isSidebarOpen}
        showSearch={true}
        showActions={enableHeaderBlocks}
        showNotifications={enableHeaderBlocks}
        toggleSidebar={() => dispatch(toggleSidebar())}
        app={app}
        user={user}
      />
    )
    notificationComponents = (
      <div>
        <NotificationManager location={props.location} />
        <UpdateInformer {...app} />
        <UINotificationSystem />
        {!app.launchedOnMobileDevice && <TrialWatcher />}
      </div>
    )
  }

  if (!isHeaderVisible) {
    header = null
  }

  const sessionWatcher = !isPublicPage ? <SessionWatcher /> : null
  const preloadManager = !isPublicPage ? <PreloadManager /> : null
  const appClass = cn({
    App: !isPublicPage,
  })
  const hasSidebar = !isPublicPage && !isLoginPage && !isPreviewPage
  // const sidebarCanSlide = app.mobileDeviceType !== PHONE && hasSidebar

  let appNode = (
    <>
      {banners.length > 0 && (
        <div className="AppWrapper_Banner">
          {banners.map(banner => banner.element)}
        </div>
      )}
      <div
        className={appClass}
        data-player-visible={Boolean(playerVisible)}
        data-banners-height-px={bannersHeightPx}
        // Inline style is required for dynamic CSS variables.
        // --banners-height-px: adjusts layout when banners are visible in the header
        // --playlist-creator-width: offsets search page content when Playlist Creator is open
        // eslint-disable-next-line react/forbid-dom-props
        style={
          {
            '--banners-height-px': `${bannersHeightPx}px`,
            '--playlist-creator-width':
              isSearchPage && isSearchPageShortlistOpen
                ? `${playlistCreatorWidth}px`
                : '0px',
          } as React.CSSProperties
        }
      >
        {header}
        <div
          className={cn({
            AppWrapper: !isPublicPage,
            AppWrapper_SidebarCanSlide: sidebarCanSlide,
            AppWrapper_Sidebar: sidebarCanSlide && hasSidebar,
            AppWrapper_SidebarOpen:
              sidebarCanSlide && isSidebarOpen && hasSidebar,
            AppWrapper_SearchCompactPage: isSearchPage,
          })}
        >
          {enableHeaderBlocks ? (
            <>
              {showHomeComponent && <Home bannersHeightPx={bannersHeightPx} />}
              {handler}
            </>
          ) : (
            handler
          )}
          {hasSidebar && (
            <React.Fragment>
              <Sidebar
                bannersHeightPx={bannersHeightPx}
                enableHeaderBlocks={enableHeaderBlocks}
                sidebarOpen={isSidebarOpen}
                pathname={pathname}
              />
              <Player
                TrackActionsComponent={TrackActions}
                mediaSource="in-app"
              />
            </React.Fragment>
          )}
          {showPlaylistCreator && (
            <ShortlistStack isBannerVisible={Boolean(bannersVisible)} />
          )}
        </div>
        <CustomCss />
        <Offline />
        {!isPublicPage && (
          <CookieBanner
            location={
              isSettingsPage ? 'Settings' : isLoginPage ? 'Login' : 'Explorer'
            }
          />
        )}
        <iframe name="download-frame" className="download-frame" />
        {notificationComponents}
        {sessionWatcher}
        {preloadManager}
        {isSupportMenuFeatureEnabled && !isPublicPage && !isLoginPage && (
          <HelpMenu />
        )}
      </div>
      <CustomDragLayer />
    </>
  )

  return (
    <ErrorBoundary errorHandler={appRedirect}>
      {appNode}
      <MobileBreakpoints />
    </ErrorBoundary>
  )
}

function isPathnameLoginRoute(pathname: string) {
  return (
    pathname.indexOf('/login') === 0 ||
    pathname.indexOf('/welcome') === 0 ||
    pathname.indexOf('/invitation') === 0 ||
    pathname.indexOf('/password-reset') === 0 ||
    pathname.indexOf('/instructions-sent') === 0 ||
    pathname.indexOf('/profile-deleted') === 0 ||
    pathname.indexOf('/forgot-password') === 0
  )
}

const isPathnameWithHiddenHeader = (pathname: string) => {
  return (
    pathname.startsWith('/search') ||
    pathname.startsWith('/invitation') ||
    pathname.startsWith('/discover')
  )
}
